import { paymentPriceDetails } from 'shared';
import { computed } from 'vue';
import { compact, flatten, toSafeInteger } from 'lodash-es';
import type { Payments } from '@aviata/platform-types';

import { useOrderQuery } from '@/entities/order';
import { useBill } from '@/entities/bill';

export function useOrderPricing(orderId: string) {
  const orderQuery = useOrderQuery(orderId);
  const {
    bill,
    isRefetching: isBillRefetching,
    isLoading: isBillLoading,
  } = useBill({ orderId });

  const isRefetching = computed(
    () =>
      (orderQuery.isSuccess.value && orderQuery.isFetching.value) ||
      isBillLoading.value ||
      isBillRefetching.value,
  );

  // TODO: use more robust pricing lib (with flat structure and dynamics products/modifiers)
  const pricingDetails = computed(() => {
    return orderQuery.data.value
      ? paymentPriceDetails.lib.getPriceDetails(orderQuery.data.value.products)
      : null;
  });

  const productsPackage = computed(() => {
    return pricingDetails.value?.additionalProducts.productsPackage || null;
  });

  const separateProducts = computed(() => {
    const raw = pricingDetails.value?.additionalProducts.separateProducts;
    if (!raw) return null;

    let flightDelayPrice = 0;
    let flightDelayEntry: (typeof raw)[number] | null = null;
    const result: typeof raw = [];

    for (const product of raw) {
      if (product.type === 'products.flight-delay-insurance') {
        flightDelayPrice += Number(product.price ?? 0);
        flightDelayEntry ??= product;
      } else {
        result.push(product);
      }
    }

    if (flightDelayEntry) {
      result.push({ ...flightDelayEntry, price: flightDelayPrice });
    }

    return result;
  });

  const promocodePrice = computed(() => {
    if (!bill.value) {
      return null;
    }

    const promocodeModifier = bill.value?.dynamic_modifiers.find(
      modifier =>
        (modifier.modifier_type as  // TODO: Add to aviatakz libs
          | Payments.Bill.DynamicModifier['modifier_type']
          | 'PROMO_CODE') === 'PROMO_CODE',
    );

    return promocodeModifier ? toSafeInteger(promocodeModifier?.amount) : null;
  });

  const loyaltyCashbackAmount = computed(() => {
    if (!bill.value) {
      return null;
    }

    const cashbackModifier = bill.value?.dynamic_modifiers.find(
      modifier => modifier.modifier_type === 'CASHBACK',
    );

    return cashbackModifier ? toSafeInteger(cashbackModifier?.amount) : null;
  });

  const totalPrice = computed(() => {
    return bill.value?.amount || '';
  });

  const entriesCount = computed(() => {
    return compact(
      flatten([
        pricingDetails.value?.ticketPrice,
        pricingDetails.value?.baggagePrice,
        pricingDetails.value?.seatMapPrice,
        productsPackage.value?.title && productsPackage.value,
        promocodePrice.value,
        loyaltyCashbackAmount.value,
        separateProducts.value,
      ]),
    ).length;
  });

  return {
    isRefetching,
    pricingDetails,
    productsPackage,
    separateProducts,
    promocodePrice,
    loyaltyCashbackAmount,
    totalPrice,
    entriesCount,
  };
}
